import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import React from 'react'
import {
  REVISION_QUERY_KEY,
  REVISION_UPDATE_QUERY_KEY,
  REVISION_UPDATE_URL,
  REVISIONS_QUERY_KEY,
} from './queryConstants'
import { showToast } from '../../../common/utils'
import { ROTA_OVERVIEW_QUERY_KEY } from './useRotaOverview'

dayjs.extend(isoWeek)

type UseRevisionUpdateReturn = {
  updateRevision?: (values: UpdateRevisionPayload) => Promise<unknown>
  isLoading: boolean
}

type UseRevisionUpdateProps = {
  id?: string
  selectedDate?: Date
  intervalType?: 'daily' | 'weekly'
  onUpdateSuccess?: () => void
}

export type UpdateRevisionPayload = {
  title?: string
  date?: string
  startTime?: string
  endTime?: string
  startDate?: string
  endDate?: string
  plannedDuration?: number
  fundingTypeId?: string
  description?: string
  addressId?: string
  flexiType?: boolean
  eventId: string
  teamMembers?: {
    add?: { teamMemberId?: string; role?: string }[]
    remove?: string[]
    doubleBooked?: string[]
    allowDoubleBooking?: boolean
  }
  undoAction?: React.ReactNode
  skipSuccessToast?: boolean
}

type UpdateRevisionResponse = any

const useRevisionUpdate = ({
  onUpdateSuccess,
}: UseRevisionUpdateProps = {}): UseRevisionUpdateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: err?.message || err?.error,
      type: 'error',
    })
  }

  const handleSuccessWithUndo = (
    message: string,
    undoAction?: React.ReactNode,
  ) => {
    showToast({
      title: message,
      type: 'success',
      action: undoAction,
    })
  }

  const updateFunction = async (
    values: UpdateRevisionPayload,
  ): Promise<UpdateRevisionResponse> => {
    const { eventId, undoAction, skipSuccessToast, ...payload } = values

    // Store undo action and skip flag for success handler
    const currentUndoAction = undoAction
    const shouldSkipToast = skipSuccessToast

    const result = await postJSON({
      url: `${REVISION_UPDATE_URL}/${eventId}`,
      method: 'PUT',
      json: payload,
      isRota: true,
    })

    // Pass undo action and skip flag to success handler
    ;(result as any)._undoAction = currentUndoAction
    ;(result as any)._skipSuccessToast = shouldSkipToast
    return result
  }

  const { mutateAsync: updateMutation, isLoading } = useMutation<
    UpdateRevisionResponse,
    unknown,
    UpdateRevisionPayload,
    unknown
  >([REVISION_UPDATE_QUERY_KEY], updateFunction, {
    onSuccess: (data) => {
      // Skip success toast if requested (for undo operations)
      if ((data as any)?._skipSuccessToast) {
        if (onUpdateSuccess) {
          onUpdateSuccess?.()
        } else {
          queryClient.invalidateQueries([REVISION_QUERY_KEY])
          queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
          queryClient.invalidateQueries([ROTA_OVERVIEW_QUERY_KEY])
        }
        return
      }

      if (onUpdateSuccess) {
        onUpdateSuccess?.()

        handleSuccessWithUndo(
          'Revision updated successfully',
          (data as any)?._undoAction,
        )

        return
      }
      queryClient.invalidateQueries([REVISION_QUERY_KEY])
      queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
      handleSuccessWithUndo(
        'Revision updated successfully',
        (data as any)?._undoAction,
      )
    },
    onError: (err) => handleError(err),
  })

  const handleUpdate = async (values: any) => {
    try {
      const result = await updateMutation(values)
      return result
    } catch (err) {
      return err
    }
  }

  return {
    updateRevision: handleUpdate,
    isLoading,
  }
}

export default useRevisionUpdate
